import { useEffect, useMemo, useRef, useState } from "react";
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    MenuItem,
    TextField,
    Typography
} from "@mui/material";
import { useFormik } from "formik";
import { useTranslation } from "../../../i18n/useTranslation";
import { createRegisterValidationSchema } from "./RegisterForm.validation";
import { Language } from "../../../shared/models";
import { getLanguageCode, getLanguages } from "../../../shared/services/language.service";
import { Country } from "../../../components/order/models";
import axios, { AxiosError } from "axios";
import { getCountries } from "../../../components/order/services";
import { getCountryName } from "../../../shared/services/country.service";
import ReCAPTCHA from "react-google-recaptcha";
import { RegisterDto } from "../../../auth/register/models";
import { COMPANY_ACCOUNT_TYPE } from "../../../shared/constants/account-type.constants";
import ErrorAlert from "../../../shared/alerts/error-alert";
import { useNavigate } from "react-router-dom";
import { FormSetting } from "../../../containers/cms-container/models";
import { getFormSetting } from "../../../containers/cms-container/services";
import { toast } from "react-toastify";

interface RegisterFormValues {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    language: string;

    companyName: string;
    streetName: string;
    buildingNumber: string;
    flatNumber: string;
    postalCode: string;
    city: string;
    country: string;
    vatNumber: string;

    password: string;
    passwordConfirmation: string;

    newsletter: boolean;
    termsAccepted: boolean;
}

const RegisterForm = () => {
    const { t, i18n  } = useTranslation('translation');
    const [languages, setLanguages] = useState<Language[]>([]);
    const [countries, setCountries] = useState<Country[]>([]);
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const [errorAlertIsOpen, setErrorAlertIsOpen] = useState(false);
    const [errorTranslateCode, setErrorTranslateCode] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [formSetting, setFormSetting] = useState<FormSetting>();
    
    const validationSchema = useMemo(
        () => createRegisterValidationSchema(t),
        [t]
    );

    useEffect(() => {
        handleSetLanguages();
        handleSetCountries();
        fetchFormSetting();
    }, [])

    const fetchFormSetting = (): void => {
        getFormSetting().then((response: any) => {
            const formSettings = response?.data as FormSetting;
            setFormSetting(formSettings);
        })
    }

    const handleSetLanguages = () => {
        getLanguages().then((result) => {
            if (!result) {
                return;
            }

            const languages =
                (result.data as Language[]) ?? [];

            setLanguages(languages);
        });
    };

    const handleSetCountries = () => {
        getCountries().then((result) => {
            if (!result) {
                return;
            }

            const countries =
                (result.data as Country[]) ?? [];

            setCountries(
                countries.filter(
                    (country) => country.code !== "FL"
                )
            );
        });
    };

    const sortedCountries = useMemo(() => {
        return [...(countries ?? [])].sort(
            (a, b) =>
                getCountryName(
                    a.code,
                    getLanguageCode()
                ).localeCompare(
                    getCountryName(
                        b.code,
                        getLanguageCode()
                    )
                )
        );
    }, [countries]);

    const prepareRegisterDto = (values: RegisterFormValues): RegisterDto => {
        const registerDto: RegisterDto = {
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            password: values.password,
            redirectUrl: process.env.REACT_APP_WEB_SHOP_LOGIN_PAGE as string,
            language: values.language,
            companyName: values.companyName,
            accountType: COMPANY_ACCOUNT_TYPE,
            emailHasToBeConfirmed: formSetting?.confirmEmail,
            hasToBeConfirmedByAdmin: formSetting?.adminNeedToAcceptUser,
            address: {
                addressType: "MAIN",
                country: values.country,
                postalCode: values.postalCode,
                city: values.city,
                streetName: values.streetName,
                buildingNumber: values.buildingNumber,
                flatNumber: values.flatNumber,
                region: "",
                addressComments: ""
            },
            contact: {
                contactType: "REPRESENTATIVE",
                title: "",
                phone: values.phone,
                mobile: "",
                website: "",
                contactComments: ""
            },
            recaptchaToken: recaptchaRef.current?.getValue() ?? null,
            vatNumber: values.vatNumber 
        }
        return registerDto;
    }

    const displaySuccessToasts = () => {
        let showAdminAcceptance = formSetting?.adminNeedToAcceptUser;
        let showEmailAcceptance = formSetting?.confirmEmail;

        if (showAdminAcceptance && showEmailAcceptance) {
            toast.success(t('register.emailSentAndConfirmationNeed'));
            return;
        }

        if (showAdminAcceptance) {
            toast.success(t('register.registrationConfirmationNeed'));
            return;
        }
        if (showEmailAcceptance) {
            toast.success(t('register.registrationEmailSent'));
            return;
        }

        if (!showAdminAcceptance && !showEmailAcceptance) {
            toast.success(t('register.registeredSuccessfully'));
            return;
        }
    }

    const formik = useFormik<RegisterFormValues>({
        initialValues: {
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            language: "",

            companyName: "",
            streetName: "",
            buildingNumber: "",
            flatNumber: "",
            postalCode: "",
            city: "",
            country: "",
            vatNumber: "",

            password: "",
            passwordConfirmation: "",

            newsletter: false,
            termsAccepted: false
        },
        validationSchema,
        onSubmit: async (values) => {
            setLoading(true);

            try {
                await axios.post(
                    "auth/register",
                    prepareRegisterDto(values)
                );
                displaySuccessToasts();
                navigate("/");
            } catch (error) {
                let translateCode = "registerForm.UNKNOWN_ERROR";

                if (error instanceof AxiosError) {
                    const data = error.response?.data;

                    if (typeof data === "string") {
                        translateCode = i18n.exists(data)
                            ? data
                            : data;
                    } else if (
                        data &&
                        typeof data === "object" &&
                        "Message" in data &&
                        typeof data.Message === "string"
                    ) {
                        translateCode = data.Message;
                    }

                    console.error(data);
                } else {
                    console.error(error);
                }

                setErrorTranslateCode(translateCode);
                setErrorAlertIsOpen(true);
            } finally {
                setLoading(false);
            }
        }
    });

    const rowSx = {
        display: "grid",
        alignItems: "center",
        columnGap: 3,
        mb: 2
    };

    const sectionTitleSx = {
        backgroundColor: "#fdf5f5",
        color: "#821818",
        textAlign: "center",
        fontWeight: 600,
        py: 0.75,
        mb: 2,
        mt: 3,
        border: "1px solid #e8caca",
        borderRadius: 1
    };

    const checkboxContainerSx = {
        display: "grid",
        gridTemplateColumns: "1fr auto",
        alignItems: "center",
        mb: 1,
        px: 2,
        py: 1.25,
        borderRadius: 2,
        bgcolor: "#fafafa",
        border: "1px solid #e5e7eb",
        transition: "all .2s ease",
        "&:hover": {
            bgcolor: "#f5f5f5",
            borderColor: "#d1d5db"
        }
    };

    return (
        <Box
            component="form"
            onSubmit={formik.handleSubmit}
            sx={{
                maxWidth: 700,
                mx: "auto"
            }}
        >
            <ErrorAlert
                isOpen={errorAlertIsOpen}
                handleCloseDialog={setErrorAlertIsOpen}
                translateCode={errorTranslateCode}
            />
            <Typography sx={sectionTitleSx}>
                {t('registerForm.CONTACT')}
            </Typography>

            <Box sx={rowSx}>
                <TextField
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    id="firstName"
                    name="firstName"
                    label={t("FIRST_NAME")}
                    variant="outlined"
                    size="small"
                    value={formik.values.firstName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                        formik.touched.firstName &&
                        Boolean(formik.errors.firstName)
                    }
                    helperText={
                        formik.touched.firstName &&
                        formik.errors.firstName
                    }
                    autoComplete="given-name"
                />
            </Box>

            <Box sx={rowSx}>
                <TextField
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    id="lastName"
                    name="lastName"
                    label={t("LAST_NAME")}
                    variant="outlined"
                    size="small"
                    value={formik.values.lastName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                        formik.touched.lastName &&
                        Boolean(formik.errors.lastName)
                    }
                    helperText={
                        formik.touched.lastName &&
                        formik.errors.lastName
                    }
                    autoComplete="family-name"
                />
            </Box>

            <Box sx={rowSx}>
                <TextField
                    InputLabelProps={{shrink: true}}
                    fullWidth
                    id="email"
                    name="email"
                    label={t("EMAIL")}
                    variant="outlined"
                    size="small"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                    autoComplete="username"
                />
            </Box>

            <Box sx={rowSx}>
                <TextField
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    id="password"
                    name="password"
                    label={t("PASSWORD")}
                    type="password"
                    variant="outlined"
                    size="small"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                        formik.touched.password &&
                        Boolean(formik.errors.password)
                    }
                    helperText={
                        formik.touched.password &&
                        formik.errors.password
                    }
                    autoComplete="new-password"
                />
            </Box>

            <Box sx={rowSx}>
                <TextField
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    id="passwordConfirmation"
                    name="passwordConfirmation"
                    label={t("CONFIRM_PASSWORD")}
                    type="password"
                    variant="outlined"
                    size="small"
                    value={formik.values.passwordConfirmation}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                        formik.touched.passwordConfirmation &&
                        Boolean(formik.errors.passwordConfirmation)
                    }
                    helperText={
                        formik.touched.passwordConfirmation &&
                        formik.errors.passwordConfirmation
                    }
                    autoComplete="new-password"
                />
            </Box>

            <Box sx={rowSx}>
                <TextField
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    id="phone"
                    name="phone"
                    label={t("PHONE")}
                    variant="outlined"
                    size="small"
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                        formik.touched.phone &&
                        Boolean(formik.errors.phone)
                    }
                    helperText={
                        formik.touched.phone &&
                        formik.errors.phone
                    }
                    autoComplete="tel"
                />
            </Box>

            <Box sx={rowSx}>
                <TextField
                    InputLabelProps={{ shrink: true }}
                    select
                    fullWidth
                    id="language"
                    name="language"
                    label={t("LANGUAGE")}
                    variant="outlined"
                    size="small"
                    value={formik.values.language}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                        formik.touched.language &&
                        Boolean(formik.errors.language)
                    }
                    helperText={
                        formik.touched.language &&
                        formik.errors.language
                    }
                >
                    {languages?.map((item) => (
                        <MenuItem
                            key={item.id}
                            value={item.id}
                        >
                            {t(`languages.${item.code}`)}
                        </MenuItem>
                    ))}
                </TextField>
            </Box>

            <Typography sx={sectionTitleSx}>
                {t('registerForm.COMPANY')}
            </Typography>

            <Box sx={rowSx}>
                <TextField
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    id="companyName"
                    name="companyName"
                    label={t("COMPANY_NAME")}
                    variant="outlined"
                    size="small"
                    value={formik.values.companyName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                        formik.touched.companyName &&
                        Boolean(formik.errors.companyName)
                    }
                    helperText={
                        formik.touched.companyName &&
                        formik.errors.companyName
                    }
                    autoComplete="organization"
                />
            </Box>

            <Box sx={rowSx}>
                <TextField
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    id="vatNumber"
                    name="vatNumber"
                    label={t("VAT_NUMBER")}
                    variant="outlined"
                    size="small"
                    value={formik.values.vatNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                        formik.touched.vatNumber &&
                        Boolean(formik.errors.vatNumber)
                    }
                    helperText={
                        formik.touched.vatNumber &&
                        formik.errors.vatNumber
                    }
                    autoComplete="off"
                />
            </Box>

            <Box sx={rowSx}>
                <TextField
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    id="streetName"
                    name="streetName"
                    label={t("STREET_NAME")}
                    variant="outlined"
                    size="small"
                    value={formik.values.streetName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                        formik.touched.streetName &&
                        Boolean(formik.errors.streetName)
                    }
                    helperText={
                        formik.touched.streetName &&
                        formik.errors.streetName
                    }
                    autoComplete="address-line1"
                />
            </Box>

            <Box sx={rowSx}>
                <Box
                    sx={{
                        display: "flex",
                        gap: 2
                    }}
                >
                    <Box sx={{ flex: 1 }}>
                        <TextField
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            id="buildingNumber"
                            name="buildingNumber"
                            label={t("BUILDING_NUMBER")}
                            variant="outlined"
                            size="small"
                            value={formik.values.buildingNumber}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                                formik.touched.buildingNumber &&
                                Boolean(formik.errors.buildingNumber)
                            }
                            helperText={
                                formik.touched.buildingNumber
                                    ? formik.errors.buildingNumber
                                    : undefined
                            }
                        />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <TextField
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            id="flatNumber"
                            name="flatNumber"
                            label={t("ADDITION")}
                            variant="outlined"
                            size="small"
                            value={formik.values.flatNumber}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                                formik.touched.flatNumber &&
                                Boolean(formik.errors.flatNumber)
                            }
                            helperText={
                                formik.touched.flatNumber
                                    ? formik.errors.flatNumber
                                    : undefined
                            }
                        />
                    </Box>
                </Box>
            </Box>
            <Box sx={rowSx}>
                <TextField
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    id="postalCode"
                    name="postalCode"
                    label={t("ZIP_CODE")}
                    variant="outlined"
                    size="small"
                    value={formik.values.postalCode}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                        formik.touched.postalCode &&
                        Boolean(formik.errors.postalCode)
                    }
                    helperText={
                        formik.touched.postalCode &&
                        formik.errors.postalCode
                    }
                    autoComplete="postal-code"
                />
            </Box>

            <Box sx={rowSx}>
                <TextField
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    id="city"
                    name="city"
                    label={t("CITY")}
                    variant="outlined"
                    size="small"
                    value={formik.values.city}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                        formik.touched.city &&
                        Boolean(formik.errors.city)
                    }
                    helperText={
                        formik.touched.city &&
                        formik.errors.city
                    }
                    autoComplete="address-level2"
                />
            </Box>

            <Box sx={rowSx}>
                <Autocomplete
                    fullWidth
                    options={sortedCountries}
                    value={
                        sortedCountries.find(
                            country =>
                                country.id === formik.values.country
                        ) ?? null
                    }
                    getOptionLabel={(option) =>
                        getCountryName(
                            option.code,
                            getLanguageCode()
                        )
                    }
                    filterOptions={(options, state) => {
                        const search = state.inputValue
                            .trim()
                            .toLowerCase();

                        return options.filter((option) => {
                            const countryName =
                                getCountryName(
                                    option.code,
                                    getLanguageCode()
                                ).toLowerCase();

                            return (
                                countryName.includes(search) ||
                                option.code
                                    .toLowerCase()
                                    .includes(search)
                            );
                        });
                    }}
                    onChange={(_, value) => {
                        formik.setFieldValue(
                            "country",
                            value?.id ?? ""
                        );
                    }}
                    onBlur={() => {
                        formik.setFieldTouched(
                            "country",
                            true
                        );
                    }}
                    isOptionEqualToValue={(
                        option,
                        value
                    ) => option.id === value.id}
                    renderOption={(props, option) => (
                        <li {...props}>
                            {getCountryName(
                                option.code,
                                getLanguageCode()
                            )}
                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            InputLabelProps={{
                                shrink: true
                            }}
                            id="country"
                            name="country"
                            label={t("COUNTRY")}
                            variant="outlined"
                            size="small"
                            error={
                                formik.touched.country &&
                                Boolean(
                                    formik.errors.country
                                )
                            }
                            helperText={
                                formik.touched.country &&
                                formik.errors.country
                            }
                        />
                    )}
                />
            </Box>

            <Typography sx={sectionTitleSx}>
                {t('registerForm.CONFIRMATION_AND_ACCEPTANCE')}
            </Typography>

            <Box
               sx={checkboxContainerSx}
            >
                <Typography>
                        {t("registerForm.RECEIVE_NEWSLETTER")}
                </Typography>

                <Checkbox
                    name="newsletter"
                    checked={formik.values.newsletter}
                    onChange={formik.handleChange}
                    disableRipple
                    sx={{
                        pl: 1,
                        pr: 0,
                        py: 0
                    }}
                />
            </Box>

            <Box sx={{ mb: 3 }}>
                <Box
                    sx={checkboxContainerSx}
                >
                    <Typography>
                        {t("registerForm.I_ACCEPT_THE_TERMS_AND_CONDITIONS")}
                    </Typography>

                    <Checkbox
                        name="termsAccepted"
                        checked={formik.values.termsAccepted}
                        onChange={formik.handleChange}
                        disableRipple
                        sx={{
                            pl: 1,
                            pr: 0,
                            py: 0
                        }}
                    />
                    {formik.errors.termsAccepted && (
                        <Typography
                            variant="caption"
                            color="error"
                            sx={{
                                mt: 1
                             }}
                        >
                            {formik.errors.termsAccepted}
                        </Typography>
                    )}
                </Box>
            </Box>

            {
                formSetting?.useCaptcha && (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            mb: 2
                        }}
                    >
                        <ReCAPTCHA
                            ref={recaptchaRef}
                            sitekey={process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY!}
                            hl={getLanguageCode()}
                        />
                    </Box>
                )
            }

            <Box
                display="flex"
                justifyContent="flex-end"
            >
                <Button
                    variant="contained"
                    type="submit"
                    disabled={loading}
                    sx={{
                        minWidth: 140,
                        bgcolor: "#821818",
                        "&:hover": {
                            bgcolor: "#6d1414"
                        },
                        "&.Mui-disabled": {
                            bgcolor: "#821818",
                            opacity: 0.7,
                            color: "#fff"
                        }
                    }}
                >
                    {loading ? (
                        <CircularProgress
                            size={22}
                            thickness={5}
                            color="inherit"
                        />
                    ) : (
                        t("registerForm.SUBMIT")
                    )}
                </Button>
            </Box>
        </Box>
    );
};

export default RegisterForm;